/**
 * Qawaem AI Agent — main orchestrator (singleton)
 *
 * Cloud: intent + natural language (Cloudflare Workers AI)
 * Local: confirmed writes via agentLocalExecutor
 */

import {
  AGENT_CONTEXT_HISTORY_LIMIT,
  AGENT_HISTORY_LIMIT,
} from '../config/agentConfig';
import { resolveAgentLicense } from './agentLicense';
import { buildAgentLocalContext } from './agentContextBuilder';
import {
  sendAgentChatRequest,
  sendAgentDocumentExtractionRequest,
  sendAgentTranscriptionRequest,
} from './agentCloudClient';
import { executeAgentAction } from './agentLocalExecutor';
import {
  detectLocalAgentAction,
  buildLocalConfirmationMessage,
} from './agentLocalIntent';
import { fileToBase64 } from '../utils/fileToBase64';
import { syncAgentVectorIndex } from './agentVectorizeSync';
import {
  sendHermesChatRequest,
  checkHermesHealth,
  fetchPendingApprovals,
  approveHermesDraft,
  rejectHermesDraft,
} from './hermesClient';
import { AGENT_ENGINE } from '../config/agentConfig';
import { normalizePlanModeResult } from './agentPlanFallback';
import i18n from '../../i18n';

const sp = (key, opts) => i18n.t(`modules.shared.aiAgent.chatService.${key}`, opts);

class AIAgentService {
  constructor() {
    this.isInitialized = false;
    this.companyId = null;
    this.licenseKey = null;
    this.conversationHistory = [];
    this.isProcessing = false;
    this.listeners = [];
    this.pendingAction = null;
    this.vectorizeSyncPromise = null;
    this.engineStatus = { engine: AGENT_ENGINE, hermesAvailable: false };
  }

  async refreshEngineStatus() {
    if (AGENT_ENGINE === 'cloud') {
      this.engineStatus = { engine: 'cloud', hermesAvailable: false };
      return this.engineStatus;
    }
    const health = await checkHermesHealth();
    this.engineStatus = {
      engine: health.ok ? 'hermes' : AGENT_ENGINE === 'hybrid' ? 'cloud-fallback' : 'hermes-offline',
      hermesAvailable: health.ok,
      health,
    };
    this.notifyListeners('engine_status', this.engineStatus);
    return this.engineStatus;
  }

  getEngineStatus() {
    return this.engineStatus;
  }

  shouldUseHermes() {
    if (AGENT_ENGINE === 'cloud') return false;
    if (AGENT_ENGINE === 'hermes') return true;
    return this.engineStatus.hermesAvailable;
  }

  async initialize() {
    try {
      const license = await resolveAgentLicense();
      if (!license) {
        return false;
      }

      this.companyId = license.companyId;
      this.licenseKey = license.licenseKey;
      this.isInitialized = true;

      console.log('AI Agent initialized', {
        companyId: this.companyId,
        hasLicenseKey: !!this.licenseKey,
      });
      this.syncVectorIndexInBackground();
      await this.refreshEngineStatus();
      return true;
    } catch (error) {
      console.error('AI Agent initialization error:', error);
      return false;
    }
  }

  async syncVectorIndexInBackground(force = false) {
    if (!this.isInitialized) {
      return { success: false, error: sp('agent_not_initialized') };
    }

    if (this.vectorizeSyncPromise && !force) {
      return this.vectorizeSyncPromise;
    }

    this.vectorizeSyncPromise = syncAgentVectorIndex({
      companyId: this.companyId,
      licenseKey: this.licenseKey,
      force,
    }).finally(() => {
      this.vectorizeSyncPromise = null;
    });

    return this.vectorizeSyncPromise;
  }

  async sendMessage(message, options = {}) {
    const moduleId = options.moduleId || 'general';
    const mode = options.mode || 'agent';
    const readOnlyMode = mode === 'ask' || mode === 'analyse' || mode === 'plan';
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) {
        return {
          success: false,
          error: sp('agent_unavailable'),
        };
      }
    }

    if (this.isProcessing) {
      return {
        success: false,
        error: sp('wait_previous'),
      };
    }

    this.isProcessing = true;
    this.notifyListeners('message_sent', { message });

    try {
      if (AGENT_ENGINE !== 'cloud') {
        await this.refreshEngineStatus();
      }

      const localAction = readOnlyMode ? null : detectLocalAgentAction(message);
      if (localAction?.requires_confirmation) {
        this.pendingAction = localAction;
        const confirmationMessage = buildLocalConfirmationMessage(localAction);
        const response = sp('local_action_detected');

        this.conversationHistory.push({
          role: 'user',
          content: message,
          timestamp: new Date().toISOString(),
        });
        this.conversationHistory.push({
          role: 'assistant',
          content: response,
          timestamp: new Date().toISOString(),
        });

        this.notifyListeners('message_received', { response });

        return {
          success: true,
          response,
          actionDetected: localAction,
          needsConfirmation: true,
          confirmationMessage,
        };
      }

      const localData = await buildAgentLocalContext(moduleId);

      let data;
      const sessionKey = options.sessionKey || `${this.companyId || 'demo'}:default`;
      const useHermes = this.shouldUseHermes();

      if (useHermes) {
        const hermesResult = await sendHermesChatRequest({
          message,
          moduleId,
          mode,
          history: this.conversationHistory.slice(-AGENT_CONTEXT_HISTORY_LIMIT),
          localData,
          sessionId: options.sessionId,
          sessionKey,
        });

        const pending = await fetchPendingApprovals(options.sessionId);
        const latestPending = pending?.items?.[0];

        data = {
          response: hermesResult.response,
          engine: 'hermes',
          decisionPackage: hermesResult.decisionPackage || latestPending,
          needs_confirmation: !!latestPending || !!hermesResult.decisionPackage?.approvalId,
          action_detected: latestPending
            ? {
                type: latestPending.actionType,
                moduleId: latestPending.moduleId,
                approvalId: latestPending.approvalId,
                params: latestPending.payload,
                hermesDraft: true,
              }
            : null,
          confirmation_message: latestPending
            ? `${latestPending.title}\n${latestPending.recommendation || ''}`
            : null,
        };
      } else {
        data = await sendAgentChatRequest({
          message,
          companyId: this.companyId,
          licenseKey: this.licenseKey,
          moduleId,
          context: {
            moduleId,
            agentMode: mode,
            history: this.conversationHistory.slice(-AGENT_CONTEXT_HISTORY_LIMIT),
            localData,
          },
        });
      }

      let responsePayload = {
        response: data.response,
        command: data.command,
        executionResult: data.executionResult,
        actionDetected: data.action_detected || data.actionDetected,
        needsConfirmation: data.needs_confirmation || data.needsConfirmation,
        confirmationMessage: data.confirmation_message || data.confirmationMessage,
        decisionPackage: data.decisionPackage || null,
        engine: data.engine || (useHermes ? 'hermes' : 'cloud'),
        plan: data.plan || null,
        planStatus: data.plan_status || data.plan?.status || null,
      };

      if (mode === 'plan') {
        const normalized = normalizePlanModeResult(message, data, localData);
        responsePayload = {
          ...responsePayload,
          ...normalized,
        };
      }

      this.conversationHistory.push({
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      });

      this.conversationHistory.push({
        role: 'assistant',
        content: responsePayload.response,
        timestamp: new Date().toISOString(),
      });

      if (this.conversationHistory.length > AGENT_HISTORY_LIMIT) {
        this.conversationHistory = this.conversationHistory.slice(-AGENT_HISTORY_LIMIT);
      }

      if (responsePayload.needsConfirmation && responsePayload.actionDetected) {
        this.pendingAction = responsePayload.actionDetected;
      }

      this.notifyListeners('message_received', data);

      return {
        success: true,
        ...responsePayload,
      };
    } catch (error) {
      console.error('AI Agent chat error:', error);

      const fallbackAction = readOnlyMode ? null : detectLocalAgentAction(message);
      if (fallbackAction?.requires_confirmation) {
        this.pendingAction = fallbackAction;
        const confirmationMessage = buildLocalConfirmationMessage(fallbackAction);
        const response =
          sp('cloud_unavailable_local');

        this.notifyListeners('message_received', { response });

        return {
          success: true,
          response,
          actionDetected: fallbackAction,
          needsConfirmation: true,
          confirmationMessage,
          cloudUnavailable: true,
        };
      }

      this.notifyListeners('message_error', { error: error.message });

      return {
        success: false,
        error: error.message || sp('communication_error'),
      };
    } finally {
      this.isProcessing = false;
    }
  }

  async confirmPendingAction() {
    if (!this.pendingAction) {
      return { success: false, error: sp('no_pending_action') };
    }

    const action = this.pendingAction;
    this.pendingAction = null;

    if (action.hermesDraft && action.approvalId) {
      const result = await approveHermesDraft(action.approvalId);
      this.notifyListeners('action_executed', result);
      return result.execution || result;
    }

    const result = await executeAgentAction(action);
    this.notifyListeners('action_executed', result);
    return result;
  }

  async rejectPendingAction() {
    if (!this.pendingAction) {
      return { success: false, error: sp('no_pending_action') };
    }

    const action = this.pendingAction;
    this.pendingAction = null;

    if (action.hermesDraft && action.approvalId) {
      const result = await rejectHermesDraft(action.approvalId);
      this.notifyListeners('action_rejected', result);
      return result;
    }

    return { success: true, cancelled: true };
  }

  cancelPendingAction() {
    this.pendingAction = null;
  }

  buildPlanExecutionPrompt(plan) {
    if (!plan?.steps?.length) {
      return sp('plan_execute_fallback', {
        objective: plan?.objective || plan?.markdown || '',
      });
    }

    const stepsText = plan.steps
      .map((s, i) => {
        const confirm = s.needs_confirmation ? sp('plan_step_confirmation_tag') : '';
        return sp('plan_step_line', {
          index: i + 1,
          title: s.title,
          detail: s.detail,
          confirm,
        });
      })
      .join('\n');

    return sp('plan_execute_full', {
      title: plan.title,
      objective: plan.objective,
      steps: stepsText,
    });
  }

  async executeApprovedPlan(plan, options = {}) {
    const moduleId = options.moduleId || 'general';
    const prompt = this.buildPlanExecutionPrompt(plan);
    return this.sendMessage(prompt, { moduleId, mode: 'agent' });
  }

  getPendingAction() {
    return this.pendingAction;
  }

  async executeCommand(command) {
    const result = await executeAgentAction(command);
    this.notifyListeners('action_executed', result);
    return result;
  }

  getHistory() {
    return this.conversationHistory;
  }

  clearHistory() {
    this.conversationHistory = [];
    this.pendingAction = null;
  }

  isAvailable() {
    return this.isInitialized;
  }

  getStatus() {
    return {
      available: this.isInitialized,
      processing: this.isProcessing,
      messageCount: this.conversationHistory.length,
      companyId: this.companyId,
      hasPendingAction: !!this.pendingAction,
      engine: this.engineStatus,
    };
  }

  async extractDocumentData(imageFile, documentType = 'bon_commande', imageMime = 'image/jpeg', userPrompt = '') {
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) {
        return {
          success: false,
          error: sp('agent_unavailable'),
        };
      }
    }

    try {
      const base64Image = await fileToBase64(imageFile);

      const data = await sendAgentDocumentExtractionRequest({
        base64Image,
        imageMime,
        documentType,
        userPrompt,
        companyId: this.companyId,
        licenseKey: this.licenseKey,
      });

      return {
        success: true,
        extractedData: data.extractedData,
        confidence: data.confidence || 0.9,
        model: data.model,
      };
    } catch (error) {
      console.error('AI Agent document extraction error:', error);
      return {
        success: false,
        error: error.message || sp('document_extraction_error'),
      };
    }
  }

  async transcribeVoiceAudio(base64Audio, language = i18n.language || 'ar') {
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) {
        throw new Error(sp('voice_license_required'));
      }
    }

    const data = await sendAgentTranscriptionRequest({
      audio: base64Audio,
      language,
      companyId: this.companyId,
      licenseKey: this.licenseKey,
    });

    return {
      success: true,
      text: data.text,
      model: data.model,
    };
  }

  addListener(callback) {
    this.listeners.push(callback);
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter((l) => l !== callback);
  }

  notifyListeners(event, data) {
    this.listeners.forEach((callback) => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('AI Agent listener error:', error);
      }
    });
  }
}

const aiAgentService = new AIAgentService();

/** @deprecated Use aiAgentService — kept for existing imports */
export const aiService = aiAgentService;

export { aiAgentService };
export default aiAgentService;
